import datetimeDecorator from 'src/decorators/datetime.decorator';
import { ShipmentOrderWaypointTimeEntity } from 'src/entities/ShipmentOrderResponse.entity';
import { isValidLoadingUnloadingDateRange } from 'src/utils/shipmentOrder.util';

/** ShipmentDetail の保存処理と同一の「処理済み」時間枠（_destroy 付き） */
export type ProcessedWaypointTime = ShipmentOrderWaypointTimeEntity & { _destroy?: boolean };

/**
 * 積地・降地の時間枠を、伝票詳細保存と同じルールで正規化する（終了が開始より前なら翌日扱いなど）。
 */
export const processWaypointTimes = (
  originalTimes: ShipmentOrderWaypointTimeEntity[],
  modifiedTimes: ShipmentOrderWaypointTimeEntity[],
): ProcessedWaypointTime[] => {
  const processedTimes = originalTimes.map((originalTime, index) => {
    const modifiedTime = modifiedTimes[index];
    if (!modifiedTime) return { ...originalTime, _destroy: true };

    let endAt = modifiedTime.endAt;
    if (modifiedTime.startAt && modifiedTime.endAt) {
      const startTime = datetimeDecorator.toHourMinutes(new Date(modifiedTime.startAt));
      const endTime = datetimeDecorator.toHourMinutes(new Date(modifiedTime.endAt));

      if (endTime < startTime) {
        const endDate = new Date(modifiedTime.endAt);
        endDate.setDate(endDate.getDate() + 1);
        endAt = `${datetimeDecorator.toYyyyMmDd(endDate)}T${endTime}:00`;
      }
    }

    return {
      ...originalTime,
      startAt: modifiedTime.startAt,
      endAt,
      stayingSeconds: modifiedTime.stayingSeconds,
    };
  });

  if (modifiedTimes.length > originalTimes.length) {
    for (let i = originalTimes.length; i < modifiedTimes.length; i++) {
      const modifiedTime = modifiedTimes[i];
      if (!modifiedTime) continue;

      let endAt = modifiedTime.endAt;
      if (modifiedTime.startAt && modifiedTime.endAt) {
        const startTime = datetimeDecorator.toHourMinutes(new Date(modifiedTime.startAt));
        const endTime = datetimeDecorator.toHourMinutes(new Date(modifiedTime.endAt));

        if (endTime < startTime) {
          const endDate = new Date(modifiedTime.endAt);
          endDate.setDate(endDate.getDate() + 1);
          endAt = `${datetimeDecorator.toYyyyMmDd(endDate)}T${endTime}:00`;
        }
      }

      processedTimes.push({
        ...modifiedTime,
        endAt,
        _destroy: false,
      });
    }
  }

  return processedTimes;
};

/**
 * 積日・降日の間隔が 180 日以内か（空・破棄の時間枠は除外）。
 */
export const validateLoadingUnloadingDateRangeForWaypointTimes = (
  shipmentOrderWaypointLoadingTimes: ProcessedWaypointTime[],
  shipmentOrderWaypointUnloadingTimes: ProcessedWaypointTime[],
): boolean => {
  const isValidTime = (t: ProcessedWaypointTime) =>
    !t._destroy && t.startAt && t.endAt;

  const validLoadingTimes = shipmentOrderWaypointLoadingTimes.filter(isValidTime);
  const validUnloadingTimes = shipmentOrderWaypointUnloadingTimes.filter(isValidTime);
  if (validLoadingTimes.length && validUnloadingTimes.length) {
    const loadingDate = new Date(Math.min(...validLoadingTimes.map((t) => new Date(t.startAt).getTime())));
    const unloadingDate = new Date(Math.max(...validUnloadingTimes.map((t) => new Date(t.endAt).getTime())));
    return isValidLoadingUnloadingDateRange(loadingDate, unloadingDate);
  }
  return true;
};
